import React, { useEffect, Suspense, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import axios from 'axios';
import IdleTimer from 'react-idle-timer';
import store from './store';
import { Box } from '@mui/material';
import { resetError } from './actions/ErrorAction';
import {
  sessionExpiryLogOut,
  logout,
  regenerateAAATokens,
  regenerateDCSTokens,
  verifyRole,
  getAccessTokenData
} from './actions/LoginAction';
import SnackBar from './components/Shared/Utils/SnackBar';
import * as Constants from './actions/configuration';
import SessionModal from './components/Shared/Utils/Modal/SessionModal';
import { ParentRoutes } from './routes/router';
import { getCountryList } from './actions/CountryListAction';
import ConfirmModal from './components/Shared/Utils/Modal/ConfirmModal';
import PropTypes from 'prop-types';
import reactGA from 'react-ga';
import RouteChangeTracker from './routes/RouteChangeTracker';
import idConstants from './data/idConstants';
import { portalViewData } from './data/appConstants';
import { setPortalView } from './actions/LandingAction';
import i18next from 'i18next';
import IEAlert from './components/IEAlert';
import getMetaDataDetails from './utils/getMetaDataDetails';
import { getCookie, getProductId } from './utils';
import DeviceBreakPoints from './deviceBreakPoints';
import { initializeHeap } from './heapAnalytics';
import Loader from './components/Shared/Utils/Loader';
// import { linkedInInsight } from './linkedInTracking';

const AppMainComponent = (props) => {
  const timeout = 1000 * 1800 * 1;
  const [showModal, setShowModal] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openIEDialog, setOpenIEDialog] = useState(false);
  // const [consent, setConsent] = useState(getParsedConsent());
  // const [consentCookie, setConsentCookie] = useState(getCookieValue('do-consent'));

  const idToken = localStorage.getItem(idConstants.tokens.aaaIdToken);
  const refreshToken = localStorage.getItem(idConstants.tokens.aaaRefreshToken);
  let idleTimer = null;

  useEffect(() => {
    if (idToken) {
      props.getAccessTokenData(idToken);
      store.dispatch({
        type: 'ACCESS_TOKEN',
        payload: idToken
      });
    }

    if (refreshToken) {
      store.dispatch({
        type: 'REFRESH_TOKEN',
        payload: refreshToken
      });
    }
  }, []);

  useEffect(() => {
    initializeHeap();
  }, []);

  // Commented out the Linkedin tracking code as it shouldn't go to production.
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const currentCookie = getCookieValue('do-consent');
  //     if (currentCookie !== consentCookie) {
  //       setConsentCookie(currentCookie);
  //       setConsent(getParsedConsent());
  //     }
  //   });

  //   return () => clearInterval(interval);
  // }, [consentCookie]);

  // useEffect(() => {
  //   // Linkedin pixel tracking Page View
  //   if (consent?.analysis === true) {
  //     linkedInInsight();
  //   }
  // }, [consent?.analysis]);

  useEffect(() => {
    let retryCount = 0;
    let apiRequestArray = [];

    axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(idConstants.tokens.aaaIdToken);

        if (!config.headers) {
          config.headers = {};
        }

        if (token) {
          config.headers.Authorization = 'bearer ' + token;
        }
        if (
          config.url?.includes(
            `${Constants.BASE_URL}/${Constants.ORDER_FULFILLMENT_SERVICE}`
          )
        ) {
          config.headers['X-tenant'] = process.env.REACT_APP_SELLER_X_TENANT;
        } else if (config.url?.includes(`${Constants.BASE_URL}/`)) {
          config.headers['X-tenant'] = process.env.REACT_APP_BUYER_X_TENANT;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      function (response) {
        apiRequestArray = [];
        return response;
      },
      function (error) {
        const originalRequest = error.config;

        if (
          error.response !== undefined &&
          error.response.status === 400 &&
          originalRequest?.url === Constants.AUTHENTICATION_URL
        ) {
          props.logout();
          return Promise.reject(error);
        }

        // refresh token flow
        if (
          originalRequest &&
          (error.message === idConstants.errorCodes.networkError ||
            error.response === undefined ||
            error.response.status === 401) &&
          !originalRequest._retry
        ) {
          setIsNetworkError(false);
          if (retryCount < 5) {
            retryCount++;
            originalRequest._retry = true;
            if (apiRequestArray.length === 0) {
              apiRequestArray.push(originalRequest);
              return props
                .regenerateAAATokens()
                ?.then(() => {
                  return axios(originalRequest);
                })
                .catch((regenerateError) => {
                  return Promise.reject(regenerateError);
                });
            } else {
              return axios(originalRequest);
            }
          } else {
            retryCount = 0;
            if (idToken && localStorage.getItem('popUpOpen') !== '1') {
              setOpenConfirmModal(true);
            }
            if (error.message === idConstants.errorCodes.networkError) {
              setIsNetworkError(true);
            }
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }.bind(this)
    );

    if (Constants.SYSTEM_ACCOUNT_AUTH_TOGGLE === 'false') {
      const originalFetch = window.fetch;
      window.fetch = function (...args) {
        return originalFetch
          .apply(this, arguments)
          .then((response) => {
            if (
              response?.status === 401 &&
              localStorage.dcsCode &&
              args[1] &&
              args[1].headers &&
              !args[1].headers.hasOwnProperty('mss-api-access-token')
            ) {
              return props
                .regenerateDCSTokens(localStorage.dcsCode)
                ?.then(() => {
                  if (args[1] && args[1].headers) {
                    args[1].headers['Authorization'] =
                      'bearer ' + localStorage.dcsJwtToken;
                  }
                  return originalFetch(args[0], {
                    ...args[1]
                  });
                })
                .catch((error) => {
                  return Promise.reject(error);
                });
            }
            return response;
          })
          .catch((error) => {
            return Promise.reject(error);
          });
      };
    }
  }, []);

  useEffect(() => {
    if (getCookie('do-consent')) {
      // Initializing Google Analytics
      reactGA.initialize(Constants.GOOGLE_ANALYTICS_TRACKING_ID);
      reactGA.pageview(window.location.pathname);
    }
    const isIE =
      window.navigator.userAgent.indexOf('MSIE') > -1 ||
      window.navigator.userAgent.indexOf('Trident') > -1;
    if (isIE) {
      setOpenIEDialog(true);
    }
    if (!document.querySelector("link[rel='canonical']")) {
      let canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = window.location.href;
      document.getElementsByTagName('head')[0].appendChild(canonicalLink);
    }
    const productIdRegex = new RegExp(/(?=[a-zA-Z0 - 9]*)/);
    const [metaTitle, metaDescription] = getMetaDataDetails(
      window.location.pathname
    );
    if (!productIdRegex.test(getProductId()) && metaTitle && metaDescription) {
      getMetaDetails(metaTitle, metaDescription);
    }
  }, []);

  useEffect(() => {
    if (getCookie('do-consent')) return;
    if (idConstants.footerLinks.includes(window.location.pathname)) return;
    document.querySelector('dock-privacy-settings')?.setAttribute('visible', 'true');
  }, [window.location.pathname]);

  const getMetaDetails = (metaTitle, metaDescription) => {
    const metaDataConstants = idConstants?.metaData;
    document
      .getElementsByTagName(metaDataConstants.metaTag)
      .namedItem(metaDataConstants.title)
      .setAttribute(metaDataConstants.content, i18next.t(metaTitle));
    document
      .getElementsByTagName(metaDataConstants.metaTag)
      .namedItem(metaDataConstants.description)
      .setAttribute(metaDataConstants.content, i18next.t(metaDescription));
    document.title = i18next.t(metaTitle);

    const ogTitleMeta = document.querySelector(
      `meta[property="${metaDataConstants.ogTitle}"]`
    );
    const ogDescriptionMeta = document.querySelector(
      `meta[property="${metaDataConstants.ogDescription}"]`
    );
    const twitterTitleMeta = document
      .getElementsByTagName(metaDataConstants.metaTag)
      .namedItem(metaDataConstants.twitterTitle);
    const twitterDescriptionMeta = document
      .getElementsByTagName(metaDataConstants.metaTag)
      .namedItem(metaDataConstants.twitterDescription);
    if (
      ogTitleMeta &&
      ogDescriptionMeta &&
      twitterTitleMeta &&
      twitterDescriptionMeta
    ) {
      ogTitleMeta.setAttribute(metaDataConstants.content, i18next.t(metaTitle));
      twitterTitleMeta.setAttribute(metaDataConstants.content, i18next.t(metaTitle));
      ogDescriptionMeta.setAttribute(
        metaDataConstants.content,
        i18next.t(metaDescription)
      );
      twitterDescriptionMeta.setAttribute(
        metaDataConstants.content,
        i18next.t(metaDescription)
      );
    }
  };

  useEffect(() => {
    props.getCountryList();
  }, []);

  const onIdle = () => {
    idleTimer?.reset();
    if (localStorage.getItem(idConstants.tokens.aaaIdToken)) {
      setShowModal(true);
    }
  };

  const handleLogout = () => {
    setShowModal(false);
    props.sessionExpiryLogOut(props?.location.pathname);
  };

  const handleOpenConfirmModal = () => {
    setOpenConfirmModal(false);
    store.dispatch({
      type: 'CONFIRM_MODAL',
      payload: false
    });
  };

  const okBtnAction = () => {
    setOpenIEDialog(false);
  };

  return (
    <>
      <IdleTimer
        ref={(ref) => {
          idleTimer = ref;
        }}
        element={document}
        onIdle={onIdle}
        debounce={250}
        timeout={timeout}
      />
      {props?.error?.data?.status !== 401 &&
        Boolean(props?.error?.data?.message) && (
          <SnackBar
            severity='error'
            changeState={() => props.resetError()}
            message={props?.error?.data?.message}
          />
        )}
      {isNetworkError && props?.accessToken ? (
        <SnackBar
          severity='error'
          changeState={() => props.resetError()}
          message={i18next.t('TOAST_MESSAGES.ERROR')}
        />
      ) : (
        ''
      )}
      {props?.error !== null &&
        props.accessToken &&
        props?.error !== undefined &&
        props?.error?.data === undefined &&
        props?.error?.message !== idConstants.errorCodes.networkError &&
        props?.error?.response?.status !== 401 && (
          <SnackBar
            severity='error'
            changeState={() => props.resetError()}
            message={
              props?.error ===
                i18next.t('TOAST_MESSAGES.SAVE_COMPANY_DETAILS_ERR.MSG') ||
              props?.error === i18next.t('TOAST_MESSAGES.SAVE_USER_DETAILS_ERR.MSG')
                ? i18next.t('TOAST_MESSAGES.INCORRECT_DETAILS_ERR.MSG')
                : i18next.t('TOAST_MESSAGES.ERROR')
            }
          />
        )}
      <Suspense fallback={<Loader />}>
        <Box className={'app' + props?.screenType}>
          <ParentRoutes />
        </Box>
        {showModal && (
          <SessionModal
            heading={i18next.t('SNACKBAR.SESSION_EXPIRED') + '!'}
            modalContent={i18next.t('SNACKBAR.SESSION_EXPIRED_MSG')}
            okOperation={handleLogout}
          />
        )}
      </Suspense>
      {(openConfirmModal || props?.confirmModal) &&
        props?.userCompanyStatus?.companyRegisteredStatus !== 'approved' && (
          <ConfirmModal
            modalContent=''
            heading={i18next.t('SNACKBAR.CANNOT_PLACE_ORDER')}
            category={'approve'}
            callback={handleOpenConfirmModal}
            okOperation={handleOpenConfirmModal}
            cancelBtn='disable'
            {...props}
          />
        )}
      {openIEDialog && <IEAlert okOperation={okBtnAction}></IEAlert>}
    </>
  );
};

const mapStateToProps = (redux_state) => ({
  error: redux_state.ErrorReducer.error,
  errorLogin: redux_state.loginReducer.errorLogin,
  userCompanyStatus: redux_state.CompanyDetailsReducer.userCompanyStatus,
  accessToken: redux_state.loginReducer.accessToken,
  confirmModal: redux_state.loginReducer.confirmModal
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      resetError,
      sessionExpiryLogOut,
      logout,
      regenerateAAATokens,
      regenerateDCSTokens,
      verifyRole,
      getCountryList,
      setPortalView,
      getAccessTokenData
    },
    dispatch
  );
}

const AppMain = (props) => {
  const { isSmallDevice, smScreen, mdScreen } = DeviceBreakPoints();
  let screenType = '';
  if (mdScreen) {
    screenType = ' md-screen';
  } else if (smScreen) {
    screenType = ' sm-screen';
  } else if (isSmallDevice) {
    screenType = ' xs-screen';
  }

  useEffect(() => {
    if (mdScreen) {
      props.setPortalView(portalViewData.DESKTOP);
    } else if (smScreen) {
      props.setPortalView(portalViewData.TAB);
    } else if (isSmallDevice) {
      props.setPortalView(portalViewData.MOBILE);
    }
  }, [screenType]);

  return (
    <>
      <AppMainComponent {...props} screenType={screenType} />
      <RouteChangeTracker />
    </>
  );
};

AppMain.propTypes = {
  width: PropTypes.oneOf(['lg', 'md', 'sm', 'xl', 'xs']).isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AppMain));
